import * as React from 'react'

import type { ErrorLike } from '@apollo/client'

import { graphql } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'

import type { CommentData } from '../../fragments/commentData'

import { useCommentReplyTracker } from './useCommentReplyTracker'
import { useCommentScoreTracker } from './useCommentScoreTracker'
import { useEngagementTimer } from './useEngagementTimer'
import { useReplySessionSync } from './useReplySessionSync'
import { useScoreSessionSync } from './useScoreSessionSync'
import type { BaseCommunityFilters } from '../../../CommunityFilters/types'
import { useCommentsQuery } from '../../../hooks/comments/useCommentsQuery'
import type { CommentsQuery } from '../../../hooks/comments/useCommentsQuery'
import { useCommentsScore } from '../../../hooks/scoring/useCommentsScore'

export type UseCommentsDataProps = {
  channelIds: string[]
  filters?: BaseCommunityFilters
}

/**
 * The Comments list query spreads `CommentData_Comment`, so its edge nodes are
 * already the shape the card tree consumes. This function is the compile-time
 * check on that: it fails if the query stops selecting the fragment.
 */
const toCommentData = (
  node: CommentsQuery['comments']['edges'][number]['node'],
): CommentData => node

export type CommentsData = {
  query: {
    data?: CommentsQuery
    loading: boolean
    error?: ErrorLike | null
    fetchingMore: boolean
    lastElementRef: (node: HTMLElement | null) => void
    fetchNewComments: () => Promise<void>
    retry: () => Promise<void>
  }
  comments: {
    list: CommentData[]
    hasComments: boolean
  }
}

export const useCommentsData = ({
  channelIds,
  filters,
}: UseCommentsDataProps): CommentsData => {
  // Query comments data
  const {
    data,
    loading,
    error,
    fetchingMore,
    lastElementRef,
    fetchNewComments,
    retry,
  } = useCommentsQuery({
    channelIds,
    filters,
  })

  const hasComments = (data?.comments?.edges?.length ?? 0) > 0
  const comments =
    data?.comments?.edges?.map((edge) => toCommentData(edge.node)) ?? []

  return {
    query: {
      data,
      loading,
      error,
      fetchingMore,
      lastElementRef,
      fetchNewComments,
      retry,
    },
    comments: {
      list: comments,
      hasComments,
    },
  }
}

export type UseCommentsEngagementDataProps = {
  channelIds: string[]
}

export type CommentsEngagementData = {
  score: {
    current: number | null
    delta: number | null
    hasIncreased: boolean
    // True while the score query is fetching. Consumers use this to gate
    // engagement empty-state rendering so they don't flash a default state
    // between "user resolved a comment" and "score refetch reveals increase".
    isLoading: boolean
  }
  engagement: {
    isActivelyEngaging: boolean
    activeTime: string
    repliedToManyComments: boolean
    commentsReplied: number
  }
}

export const useCommentsEngagementData = ({
  channelIds,
}: UseCommentsEngagementDataProps): CommentsEngagementData => {
  // Comments score data - supports multiple channels and returns aggregated summary
  const { commentsScore, loading: isScoreLoading } = useCommentsScore({
    channelIds,
  })

  // Score tracking - supports multiple channels
  // Tracks the aggregated score across all channels in the session
  const currentScore = commentsScore?.currentPeriodScore.totalValue ?? null
  const { hasScoreIncreased, scoreDelta, startSession, stopSession } =
    useCommentScoreTracker(
      !isScoreLoading && currentScore != null ? currentScore : null,
      channelIds,
    )

  // Engagement timer
  const { isActivelyEngaging, activeTime } = useEngagementTimer()

  // Reply tracking - supports multiple channels
  // Tracks the aggregated reply count across all channels in the session
  const {
    repliedToManyComments,
    commentsReplied,
    startSession: startReplySession,
    stopSession: stopReplySession,
    incrementReplyCount,
  } = useCommentReplyTracker(channelIds)

  // Handle score session management: for has score increased, we need to start a session
  useScoreSessionSync(channelIds, startSession, stopSession)

  // Handle reply session management
  useReplySessionSync(
    channelIds,
    startReplySession,
    stopReplySession,
    incrementReplyCount,
  )

  // Start reply tracking session automatically when this hook initializes
  React.useEffect(() => {
    startReplySession()
  }, [startReplySession])

  return {
    score: {
      current: currentScore,
      delta: scoreDelta,
      hasIncreased: !isScoreLoading && hasScoreIncreased,
      isLoading: isScoreLoading,
    },
    engagement: {
      isActivelyEngaging,
      activeTime,
      repliedToManyComments,
      commentsReplied,
    },
  }
}

export const UseCommentsListData_Channel = graphql(`
  fragment UseCommentsListData_Channel on Channel @_unmask {
    id
  }
`)

// Legacy type and hook for backward compatibility with single channel
export type UseCommentsListDataProps = {
  channel: FragmentOf<typeof UseCommentsListData_Channel>
  filters?: BaseCommunityFilters
}

export type CommentsListData = CommentsData & CommentsEngagementData

/**
 * @deprecated Use useCommentsData and useCommentsEngagementData separately for better flexibility
 * This hook is maintained for backward compatibility only
 */
export const useCommentsListData = ({
  channel,
  filters,
}: UseCommentsListDataProps): CommentsListData => {
  const commentsData = useCommentsData({ channelIds: [channel.id], filters })
  const engagementData = useCommentsEngagementData({ channelIds: [channel.id] })

  return {
    ...commentsData,
    ...engagementData,
  }
}
